import {StageManager} from "../StageMaganer/StageManager";
import {PublicTicketsFormDefs} from "../PublicTicketsForm";

class GlobalState {
    /** @var {PublicTicketsFormDefs.Stage[]} */
    stageArray;
    /** @var {StageManager} */
    stageManager;
    /** @var {PublicTicketsFormDefs.Stage} */
    currentStage;

    /** @var {PublicEventResource.PublicEventResource|undefined} */
    parentEvent;

    /** @var {PublicEventResource.PublicEventResource|undefined} */
    selectedDate

    /** @var {PublicEventResource.SeatType|undefined} */
    selectedSeatType

    /** @var {PublicTicketsFormDefs.ReservedSeatSelection[]} */
    selectedSeats

    /** @var {PublicTicketsFormDefs.GeneralAdmissionTicketCount[]} */
    generalAdmissionTicketCounts
    /** @var {PublicTicketsFormDefs.ReservedTicketCount[]} */
    reserveTicketCounts

    /** @var {string} */
    discountCode

    /** @var {PublicTicketsFormDefs.Addon[]} */
    addons

    /** @var {PublicTicketsFormDefs.PaymentInfo} */
    paymentInfo

    /** @var {string} */
    howDidYouFindUs


    constructor() {
        // Initial state
        this.stageArray = [
            'show-select',
            'date-select',
            'seat-select',
            'seat-type-select',
            'payment-info',
            'summary',
        ];

        this.selectedSeatType = undefined;
        this.selectedSeats = [];
        this.generalAdmissionTicketCounts = [];
        this.reserveTicketCounts = [];
        this.discountCode = '';
        this.addons = [];

        this.howDidYouFindUs = '';

        this.currentStage = 'show-select';

        // Stage Manager
        this.stageManager = new StageManager(this);
    }

    /**
     * @param {PublicEventResource.PublicEventResource} parentEvent
     */
    setParentEvent(parentEvent) {
        this.parentEvent = parentEvent

        // Clear next stage
        this.stageManager.clearStageInstance('date-select');

        this.selectedDate = undefined;
    };

    /**
     * @param {PublicEventResource.PublicEventResource} eventDate
     */
    setEventDate(eventDate) {
        this.selectedDate = eventDate;

        // Clear seat type
        this.selectedSeatType = undefined;
    }

    /**
     * @param {PublicEventResource.SeatType} seatType
     */
    setSeatType(seatType) {
        // noinspection JSValidateTypes
        this.selectedSeatType = seatType;

        // Clear next stage if seat was changed
        this.stageManager.clearStageInstance('seat-select');
        this.stageManager.clearStageInstance('seat-type-select');
    }

    /**
     * @param {PublicTicketsFormDefs.ReservedSeatSelection[]} seats
     */
    setSelectedSeats(seats) {
        this.selectedSeats = seats;

        this.stageManager.clearStageInstance('seat-type-select');
    }

    /**
     * @param {PublicTicketsFormDefs.GeneralAdmissionTicketCount[]} tickets
     */
    setGeneralAdmissionTicketCounts(tickets) {
        this.generalAdmissionTicketCounts = tickets;

        this.stageManager.clearStageInstance('payment-info');
    }

    /**
     * @param {PublicTicketsFormDefs.ReservedTicketCount[]} tickets
     */
    setReserveTicketCounts(tickets) {
        this.reserveTicketCounts = tickets;

        this.stageManager.clearStageInstance('payment-info');
    }

    /**
     * @param {PublicTicketsFormDefs.Addon[]} addons
     */
    setAddons(addons) {
        this.addons = addons;

        this.stageManager.clearStageInstance('payment-info');
    }

    /**
     * @param {string} discountCode
     */
    setDiscountCode(discountCode) {
        this.discountCode = discountCode;

        this.stageManager.clearStageInstance('payment-info');
    }

    /**
     * @param {PublicTicketsFormDefs.PaymentInfo} paymentInfo
     */
    setPaymentInfo(paymentInfo) {
        this.paymentInfo = paymentInfo;

        this.stageManager.clearStageInstance('summary');
    }

    /**
     * @param {string} howDidYouFindUs
     */
    setHowDidYouFindUs(howDidYouFindUs) {
        this.howDidYouFindUs = howDidYouFindUs;
    }

    /**
     * Returns order data in a format the server can understand
     */
    getOrderDataForServer(useRealPaymentData = false) {
        // Build visitors array
        const visitors = {};

        if (this.selectedSeatType === 'general-admission') {
            // General Admission
            this.generalAdmissionTicketCounts.forEach(ticketCount => {
                if (!visitors[ticketCount.section]) {
                    // Create section
                    visitors[ticketCount.section] = {};
                }

                // Save count with type id as key
                visitors[ticketCount.section][ticketCount.visitorTypeId] = ticketCount.count;
            })
        } else {
            // Reserved
            //// Seat list
            visitors.list = this.selectedSeats.map(seat => seat.seat);

            //// Visitor types
            this.reserveTicketCounts.forEach(ticketCount => {
                if (!visitors[ticketCount.section]) {
                    // Create section
                    visitors[ticketCount.section] = {};
                }
                if (!visitors[ticketCount.section][ticketCount.subSection]) {
                    // Create sub section
                    visitors[ticketCount.section][ticketCount.subSection] = {};
                }

                // Save count with type id as key
                visitors[ticketCount.section][ticketCount.subSection][ticketCount.visitorTypeId] = ticketCount.count;
            })

            //// Not sure what is the purpose of this array but
            //// we add it anyway
            this.selectedSeats.forEach(selectedSeat => {
                const arrayKey = `${selectedSeat.section}_${selectedSeat.subSection}_selected`;
                if (!visitors[arrayKey]) {
                    // Create section
                    visitors[arrayKey] = {};
                }

                // Break down seats in parts
                const seatParts = selectedSeat.seat.split("-");

                // And group them by their first part
                const firstPart = seatParts[0];
                if (!visitors[arrayKey][firstPart]) {
                    // Create section
                    visitors[arrayKey][firstPart] = [];
                }

                // Add to seat sub section
                visitors[arrayKey][firstPart].push(`${seatParts[1]}-${seatParts[2]}`)
            })
        }

        // Build addons array
        const addons = this.addons.map(addon => {
            return {
                id: addon.addon_id,
                count: addon.count,
            };
        })

        // Build tickets array
        const tickets = {
            visitors,

            // Legacy required fields
            cars: 0,
            motorcycles: 0,
        };

        let paymentInfo = this.paymentInfo ? this.paymentInfo : {};

        if (useRealPaymentData) {
            // Final data requested, use real payment data
            return {
                event_id: this.selectedDate.id,
                seats_type: this.selectedSeatType === 'general-admission' ? 'general_admission' : 'reserved',
                tickets,
                addons,
                discount: this.discountCode,

                first_name: paymentInfo.first_name,
                last_name: paymentInfo.last_name,
                phone: paymentInfo.phone,
                email: paymentInfo.email,
                address1: paymentInfo.address1,
                address2: paymentInfo.address2,
                city: paymentInfo.city,
                state: paymentInfo.state,
                zip: paymentInfo.zip,
                cc_number: paymentInfo.cc_number,
                cc_month: paymentInfo.cc_month,
                cc_year: paymentInfo.cc_year,
                cc_cvv: paymentInfo.cc_cvv,

                how_did_you_find_us: this.howDidYouFindUs,
            }

        } else {
            // Use fake payment data, because we may not have
            // payment info fields ready
            paymentInfo = {
                // TEST DATA
                first_name: "TEST+FIRST",
                last_name: "TEST+LAST",
                phone: "1231231231",
                email: "example@example.com",
                address1: "EXAMPLE+ADDRESS",
                address2: "EXAMPLE+ADDRESS",
                city: "EXAMPLE+CITY",
                state: "Alabama",
                zip: "12345",
                cc_number: "4007000000027",
                cc_month: "12",
                cc_year: "2030",
                cc_cvv: "123",

                // Override fake data with real payment info, if any
                ...paymentInfo,
            }

            return {
                event_id: this.selectedDate.id,
                // Fix typo in order type general-admission to 'general_admission
                seats_type: this.selectedSeatType === 'general-admission' ? 'general_admission' : 'reserved',
                tickets,
                addons,
                discount: this.discountCode,

                ...paymentInfo,
            }
        }
    }

}

export {GlobalState};
